import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userDetails, logout, isAdmin } = useAuth();

  // Check if user is in admin mode
  useEffect(() => {
    if (currentUser && userDetails?.role === 'admin') {
      setIsAdminMode(true);
    } else {
      setIsAdminMode(false);
    }
  }, [currentUser, userDetails?.role]);

  return (
    <nav className="bg-black shadow-lg">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <span className="text-sm md:text-base font-bold text-white">Mechanical Properties Characterization Lab</span>
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="flex items-baseline ml-10 space-x-4">
                <Link
                  to="/dashboard"
                  className="px-3 py-2 text-sm font-medium text-white rounded-md hover:bg-yellow-500"
                >
                  Dashboard
                </Link>
                <Link
                  to="/scan"
                  className="px-3 py-2 text-sm font-medium text-white rounded-md hover:bg-yellow-500"
                >
                  Scan QR Code
                </Link>
                <Link
                  to="/items"
                  className="px-3 py-2 text-sm font-medium text-white rounded-md hover:bg-yellow-500"
                >
                  Browse Items
                </Link>
                  {currentUser && (
                    <>
                      <Link
                        to="/my-reservations"
                        className="px-3 py-2 text-sm font-medium text-white rounded-md hover:bg-yellow-500"
                      >
                        My Reservations
                      </Link>
                    </>
                  )}
                {isAdminMode && (
                  <>
                    <Link
                      to="/admin-panel"
                      className="px-3 py-2 text-sm font-medium text-black bg-yellow-500 rounded-md hover:bg-yellow-600"
                    >
                      Admin Panel
                    </Link>
                    <Link
                      to="/students"
                      className="px-3 py-2 text-sm font-medium text-black bg-yellow-500 rounded-md hover:bg-yellow-600"
                    >
                      Manage Students
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="flex items-center ml-4 md:ml-6 space-x-3">
              {currentUser ? (
                <div className="flex items-center space-x-3">
                  {isAdminMode && (
                    <span className="flex items-center px-2.5 py-1 text-xs font-semibold bg-red-600 text-white rounded-md">
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                      </svg>
                      ADMIN
                    </span>
                  )}
                  <span className="text-sm font-medium text-white">
                    {userDetails?.provider === 'anonymous' ? (userDetails?.name || `Student ${userDetails?.pid}`) : userDetails?.name || currentUser.displayName || 'User'}
                  </span>
                  <button
                    onClick={async () => {
                      try {
                        await logout();
                        navigate('/login');
                      } catch (error) {
                        console.error('Logout error:', error);
                        // Still navigate to login even if logout fails
                        navigate('/login');
                      }
                    }}
                    className="px-3 py-2 text-sm font-medium text-blue-600 bg-white rounded-md hover:bg-gray-100"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    className="px-3 py-2 text-sm font-medium text-blue-600 bg-white rounded-md hover:bg-gray-100"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/admin"
                    className="px-3 py-2 text-sm font-medium text-gray-300 border border-gray-300 rounded-md hover:bg-gray-700"
                  >
                    Admin
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="flex -mr-2 md:hidden">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 text-white rounded-md hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Open main menu</span>
              {!isMenuOpen ? (
                <svg
                  className="block w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link
              to="/dashboard"
              className="block px-3 py-2 text-base font-medium text-white rounded-md hover:bg-yellow-500"
              onClick={() => setIsMenuOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              to="/scan"
              className="block px-3 py-2 text-base font-medium text-white rounded-md hover:bg-yellow-500"
              onClick={() => setIsMenuOpen(false)}
            >
              Scan QR Code
            </Link>
            <Link
              to="/items"
              className="block px-3 py-2 text-base font-medium text-white rounded-md hover:bg-yellow-500"
              onClick={() => setIsMenuOpen(false)}
            >
              Browse Items
            </Link>
            {currentUser && (
              <Link
                to="/my-reservations"
                className="block px-3 py-2 text-base font-medium text-white rounded-md hover:bg-yellow-500"
                onClick={() => setIsMenuOpen(false)}
              >
                My Reservations
              </Link>
            )}
            {isAdminMode && (
              <>
                <Link
                  to="/admin-panel"
                  className="block px-3 py-2 text-base font-medium text-black bg-yellow-500 rounded-md hover:bg-yellow-600"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Admin Panel
                </Link>
                <Link
                  to="/students"
                  className="block px-3 py-2 text-base font-medium text-black bg-yellow-500 rounded-md hover:bg-yellow-600"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Manage Students
                </Link>
              </>
            )}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-700">
            {currentUser ? (
              <div className="flex flex-col space-y-3 px-5">
                {isAdminMode && (
                  <div className="flex items-center justify-center">
                    <span className="flex items-center px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-md">
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                      </svg>
                      ADMIN MODE
                    </span>
                  </div>
                )}
                <div className="text-white text-center">
                  <div className="text-base font-medium">
                    {userDetails?.provider === 'anonymous' ? (userDetails?.name || `Student ${userDetails?.pid}`) : userDetails?.name || currentUser.displayName || 'User'}
                  </div>
                </div>
                <button
                  onClick={() => {
                    logout();
                    navigate('/login');
                    setIsMenuOpen(false);
                  }}
                  className="px-3 py-2 text-base font-medium text-blue-600 bg-white rounded-md hover:bg-gray-100"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex flex-col space-y-2 px-5">
                <Link
                  to="/login"
                  className="block px-3 py-2 text-base font-medium text-blue-600 bg-white rounded-md hover:bg-gray-100 text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/admin"
                  className="block px-3 py-2 text-base font-medium text-gray-300 border border-gray-300 rounded-md hover:bg-gray-700 text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Admin Access
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
